import React from 'react';
import { 
  CurrencyDollarIcon, 
  ChartBarIcon, 
  KeyIcon, 
  ClockIcon 
} from '@heroicons/react/24/outline';
import { MetricCard } from '../ui/MetricCard';
import { UsageChart } from '../charts/UsageChart';
import { ModelUsageChart } from '../charts/ModelUsageChart';
import { 
  mockDashboardStats, 
  generateChartData,
  mockModelUsage 
} from '../../services/mockData';

export const Overview: React.FC = () => {
  const chartData = generateChartData(30);
  const stats = mockDashboardStats;

  const formatCurrency = (value: number) => 
    new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(value);

  const formatNumber = (value: number) => 
    new Intl.NumberFormat('en-US').format(value);

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Requests"
          value={formatNumber(stats.totalRequests)}
          change={{ value: 12.5, type: 'increase', period: 'vs last month' }}
          icon={<ChartBarIcon className="h-6 w-6" />}
          color="blue"
        />
        
        <MetricCard
          title="Total Cost"
          value={formatCurrency(stats.totalCost)}
          change={{ value: 8.2, type: 'increase', period: 'vs last month' }}
          icon={<CurrencyDollarIcon className="h-6 w-6" />}
          color="green"
        />
        
        <MetricCard
          title="Active API Keys"
          value={stats.activeKeys}
          icon={<KeyIcon className="h-6 w-6" />}
          color="purple"
        />
        
        <MetricCard
          title="Predicted Monthly Cost"
          value={formatCurrency(stats.costPrediction.predictedEndOfMonthCost)}
          change={{ value: 15.3, type: 'increase', period: 'vs current' }}
          icon={<ClockIcon className="h-6 w-6" />}
          color="yellow"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UsageChart data={chartData} />
        <ModelUsageChart data={mockModelUsage} />
      </div>

      {/* Cost Prediction Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="chart-container">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {stats.recentActivity.slice(0, 8).map((activity, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{activity.model}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(activity.timestamp).toLocaleString()} • {activity.team}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {formatCurrency(activity.cost)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatNumber(activity.tokens)} tokens
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Cost Prediction */}
          <div className="chart-container">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Cost Prediction</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Current Month</span>
                <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {formatCurrency(stats.costPrediction.currentMonthCost)}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Predicted End of Month</span>
                <span className="text-lg font-bold text-orange-600 dark:text-orange-400">
                  {formatCurrency(stats.costPrediction.predictedEndOfMonthCost)}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Daily Average</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formatCurrency(stats.costPrediction.dailyAverage)}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Days Remaining</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {stats.costPrediction.daysRemaining}
                </span>
              </div>
              
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  stats.costPrediction.trend === 'increasing' 
                    ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300' 
                    : stats.costPrediction.trend === 'decreasing'
                    ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                }`}>
                  Trend: {stats.costPrediction.trend}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="chart-container">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full btn-primary text-left">
                View Detailed Analytics
              </button>
              <button className="w-full btn-secondary text-left">
                Manage API Keys
              </button>
              <button className="w-full btn-secondary text-left">
                Download Report
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};